import { normalizeTicketsByProduct } from "../init-game";
import {
  type AllocateTicketsAction,
  type ConfirmRoundResultAction,
  FISH_PRICE_WON_VALUES,
  FISH_PRODUCT_IDS,
  type FishProductId,
  type FishShopV2Action,
  type FishShopV2State,
  type FishTicketsByProduct,
  type LockMarketAction,
  type RestartGameAction,
  type RevealRoundAction,
  type SubmitPricesAction,
  type UseCheckAction,
  type UseSecretAction,
  type ValidationResult,
} from "../types";

const VALID_PRICE_SET = new Set<number>(FISH_PRICE_WON_VALUES);
const VALID_PRODUCT_SET = new Set<string>(FISH_PRODUCT_IDS);

const ok: ValidationResult = { valid: true };

function fail(
  error: string,
  details?: Record<string, unknown>,
): ValidationResult {
  return { valid: false, error, details };
}

function hasPlayer(state: FishShopV2State, playerId: string): boolean {
  return state.players.some((player) => player.playerId === playerId);
}

function findPlayer(state: FishShopV2State, playerId: string) {
  return state.players.find((player) => player.playerId === playerId);
}

function isHostAction(state: FishShopV2State, playerId: string): boolean {
  return state.hostPlayerId === playerId;
}

function sumTickets(ticketsByProduct: FishTicketsByProduct): number {
  return FISH_PRODUCT_IDS.reduce(
    (total, productId) => total + ticketsByProduct[productId],
    0,
  );
}

function getEligibleProductIds(
  state: FishShopV2State,
  playerId: string,
): readonly FishProductId[] {
  return FISH_PRODUCT_IDS.filter((productId) =>
    state.currentRound.market[productId].sellers.some(
      (seller) => seller.playerId === playerId,
    ),
  );
}

function getEligiblePriceSubmitterIds(
  state: FishShopV2State,
): readonly string[] {
  const ids = new Set<string>();
  for (const productId of FISH_PRODUCT_IDS) {
    for (const seller of state.currentRound.market[productId].sellers) {
      ids.add(seller.playerId);
    }
  }
  return [...ids];
}

function hasOnlyKnownProducts(
  values: Readonly<Partial<Record<FishProductId, unknown>>>,
): boolean {
  return Object.keys(values).every((productId) =>
    VALID_PRODUCT_SET.has(productId),
  );
}

export function validateAllocateTickets(
  state: FishShopV2State,
  action: AllocateTicketsAction,
): ValidationResult {
  if (state.currentRound.phase !== "TICKET_ALLOCATION") {
    return fail("Tickets can only be allocated during ticket allocation");
  }

  const player = findPlayer(state, action.playerId);
  if (!player) {
    return fail("Unknown player", { playerId: action.playerId });
  }

  if (!hasOnlyKnownProducts(action.ticketsByProduct)) {
    return fail("Unknown product in allocation");
  }

  if (state.currentRound.allocations[action.playerId]) {
    return fail("Player has already allocated tickets for this round", {
      playerId: action.playerId,
    });
  }

  const normalized = normalizeTicketsByProduct(action.ticketsByProduct);
  for (const [productId, ticketCount] of Object.entries(normalized)) {
    if (!Number.isInteger(ticketCount) || ticketCount < 0) {
      return fail("Ticket counts must be non-negative integers", {
        productId,
        ticketCount,
      });
    }
  }

  const allocatedTotal = sumTickets(normalized);
  if (allocatedTotal > player.remainingTickets) {
    return fail("Cannot allocate more tickets than the player has remaining", {
      allocatedTotal,
      remainingTickets: player.remainingTickets,
    });
  }

  return ok;
}

export function validateLockMarket(
  state: FishShopV2State,
  action: LockMarketAction,
): ValidationResult {
  if (!isHostAction(state, action.playerId)) {
    return fail("Only the host can lock the market");
  }

  if (state.currentRound.phase !== "TICKET_ALLOCATION") {
    return fail("Market can only lock after ticket allocation");
  }

  const missingPlayerIds = state.players
    .map((player) => player.playerId)
    .filter((playerId) => !state.currentRound.allocations[playerId]);
  if (missingPlayerIds.length > 0) {
    return fail("All players must allocate or pass before market lock", {
      missingPlayerIds,
    });
  }

  return ok;
}

export function validateSubmitPrices(
  state: FishShopV2State,
  action: SubmitPricesAction,
): ValidationResult {
  if (
    state.currentRound.phase !== "MARKET_REVEALED" &&
    state.currentRound.phase !== "PRICE_SUBMISSION"
  ) {
    return fail("Prices can only be submitted after market reveal");
  }

  if (!hasPlayer(state, action.playerId)) {
    return fail("Unknown player", { playerId: action.playerId });
  }

  if (!hasOnlyKnownProducts(action.prices)) {
    return fail("Unknown product in price submission");
  }

  if (state.currentRound.priceSubmissions[action.playerId]) {
    return fail("Player has already submitted prices for this round", {
      playerId: action.playerId,
    });
  }

  const eligibleProductIds = getEligibleProductIds(state, action.playerId);
  if (eligibleProductIds.length === 0) {
    return fail("Player has no products to price this round");
  }

  const submittedProductIds = Object.keys(action.prices) as FishProductId[];
  const missingProductIds = eligibleProductIds.filter(
    (productId) => action.prices[productId] === undefined,
  );
  const extraProductIds = submittedProductIds.filter(
    (productId) => !eligibleProductIds.includes(productId),
  );

  if (missingProductIds.length > 0 || extraProductIds.length > 0) {
    return fail("Submitted prices must match the player's sold products", {
      missingProductIds,
      extraProductIds,
    });
  }

  for (const productId of eligibleProductIds) {
    const priceWon = action.prices[productId];
    if (priceWon === undefined || !VALID_PRICE_SET.has(priceWon)) {
      return fail("Price must be 1000-5000 won in 1000-won increments", {
        productId,
        priceWon,
      });
    }
  }

  return ok;
}

export function validateUseSecret(
  state: FishShopV2State,
  action: UseSecretAction,
): ValidationResult {
  if (state.status !== "IN_PROGRESS") {
    return fail("Cannot use secret after the game has completed");
  }

  if (
    state.currentRound.phase === "ROUND_REVEALED" ||
    state.currentRound.phase === "RESTART_READY" ||
    state.currentRound.phase === "COMPLETED"
  ) {
    return fail("Secret must be used before round reveal");
  }

  const player = findPlayer(state, action.playerId);
  if (!player) {
    return fail("Unknown player", { playerId: action.playerId });
  }

  if (player.itemStatus !== "AVAILABLE") {
    return fail("Player has already used their one-time item");
  }

  if (!VALID_PRODUCT_SET.has(action.productId)) {
    return fail("Unknown secret target", { productId: action.productId });
  }

  return ok;
}

export function validateUseCheck(
  state: FishShopV2State,
  action: UseCheckAction,
): ValidationResult {
  if (state.status !== "IN_PROGRESS") {
    return fail("Cannot use check after the game has completed");
  }

  const player = findPlayer(state, action.playerId);
  if (!player) {
    return fail("Unknown player", { playerId: action.playerId });
  }

  if (player.itemStatus !== "AVAILABLE") {
    return fail("Player has already used their one-time item");
  }

  return ok;
}

export function validateRevealRound(
  state: FishShopV2State,
  action: RevealRoundAction,
): ValidationResult {
  if (!isHostAction(state, action.playerId)) {
    return fail("Only the host can reveal the round");
  }

  if (
    state.currentRound.phase !== "MARKET_REVEALED" &&
    state.currentRound.phase !== "PRICE_SUBMISSION"
  ) {
    return fail("Round can only be revealed after market lock");
  }

  const missingSubmitterIds = getEligiblePriceSubmitterIds(state).filter(
    (playerId) => !state.currentRound.priceSubmissions[playerId],
  );
  if (missingSubmitterIds.length > 0) {
    return fail("All sellers must submit prices before reveal", {
      missingSubmitterIds,
    });
  }

  return ok;
}

export function validateConfirmRoundResult(
  state: FishShopV2State,
  action: ConfirmRoundResultAction,
): ValidationResult {
  if (state.currentRound.phase !== "ROUND_REVEALED") {
    return fail("Round results can only be confirmed after reveal");
  }

  if (!hasPlayer(state, action.playerId)) {
    return fail("Unknown player", { playerId: action.playerId });
  }

  return ok;
}

export function validateRestartGame(
  state: FishShopV2State,
  action: RestartGameAction,
): ValidationResult {
  if (!isHostAction(state, action.playerId)) {
    return fail("Only the host can restart the game");
  }

  if (state.currentRound.phase !== "RESTART_READY") {
    return fail("Game can only restart from restart-ready state");
  }

  return ok;
}

export function validateAction(
  state: FishShopV2State,
  action: FishShopV2Action,
): ValidationResult {
  if (action.type === "AllocateTickets") {
    return validateAllocateTickets(state, action);
  }
  if (action.type === "LockMarket") {
    return validateLockMarket(state, action);
  }
  if (action.type === "SubmitPrices") {
    return validateSubmitPrices(state, action);
  }
  if (action.type === "UseSecret") {
    return validateUseSecret(state, action);
  }
  if (action.type === "UseCheck") {
    return validateUseCheck(state, action);
  }
  if (action.type === "RevealRound") {
    return validateRevealRound(state, action);
  }
  if (action.type === "ConfirmRoundResult") {
    return validateConfirmRoundResult(state, action);
  }
  if (action.type === "RestartGame") {
    return validateRestartGame(state, action);
  }
  return fail("Unknown action");
}
